import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaTimes, FaPlus, FaMinus, FaTrash, FaShoppingCart } from 'react-icons/fa';
import { closeCart, toggleCart } from '../../store/slices/uiSlice';
import { removeFromCart, updateQuantity, selectCartItems, selectCartTotal, selectCartItemCount, selectCartCurrency } from '../../store/slices/cartSlice';

const Cart = () => {
  const dispatch = useDispatch();
  const cartOpen = useSelector((state) => state.ui.cartOpen);
  const items = useSelector(selectCartItems);
  const total = useSelector(selectCartTotal);
  const itemCount = useSelector(selectCartItemCount);
  const currency = useSelector(selectCartCurrency);

  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹';

  const handleQuantityChange = (productId, newQuantity) => {
    dispatch(updateQuantity({ productId, quantity: newQuantity }));
  };

  const handleRemove = (productId) => {
    dispatch(removeFromCart(productId));
  };

  if (!cartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => dispatch(closeCart())}
      />

      {/* Cart Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-primary text-white">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FaShoppingCart />
            Shopping Cart ({itemCount})
          </h2>
          <button
            onClick={() => dispatch(closeCart())}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded transition"
          >
            <FaTimes />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <FaShoppingCart className="text-6xl mb-4 opacity-50" />
              <p className="text-lg font-semibold mb-2">Your cart is empty</p>
              <p className="text-sm text-center mb-4">Add some products to get started!</p>
              <Link
                to="/products"
                onClick={() => dispatch(closeCart())}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition"
              >
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const itemPrice = item.product.discountedPrice || item.product.price;
                const itemTotal = itemPrice * item.quantity;
                const imageUrl = item.product.images && item.product.images.length > 0 
                  ? item.product.images[0] 
                  : 'https://via.placeholder.com/200';

                return (
                  <div key={item.product._id} className="flex gap-3 p-3 border rounded-lg hover:shadow-md transition">
                    {/* Product Image */}
                    <Link
                      to={`/products/${item.product._id}`}
                      onClick={() => dispatch(closeCart())}
                      className="flex-shrink-0"
                    >
                      <img
                        src={imageUrl}
                        alt={item.product.name}
                        className="w-20 h-20 object-cover rounded"
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/products/${item.product._id}`}
                        onClick={() => dispatch(closeCart())}
                        className="block"
                      >
                        <h3 className="font-semibold text-sm mb-1 line-clamp-2 hover:text-primary transition">
                          {item.product.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600 mb-2">
                        {currencySymbol}{itemPrice.toLocaleString()} each
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => handleQuantityChange(item.product._id, item.quantity - 1)}
                          className="p-1 border rounded hover:bg-gray-100 transition"
                          disabled={item.quantity <= 1}
                        >
                          <FaMinus className="text-xs" />
                        </button>
                        <span className="px-3 py-1 border rounded min-w-[3rem] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.product._id, item.quantity + 1)}
                          className="p-1 border rounded hover:bg-gray-100 transition"
                          disabled={item.product.stock && item.quantity >= item.product.stock}
                        >
                          <FaPlus className="text-xs" />
                        </button>
                      </div>

                      {/* Stock Warning */}
                      {item.product.stock && item.quantity >= item.product.stock && (
                        <p className="text-xs text-orange-600 mb-1">
                          Max available: {item.product.stock}
                        </p>
                      )}

                      {/* Item Total & Remove */}
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-primary">
                          {currencySymbol}{itemTotal.toLocaleString()}
                        </span>
                        <button
                          onClick={() => handleRemove(item.product._id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                          title="Remove from cart"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {items.length > 0 && (
          <div className="border-t p-4 bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold">Total:</span>
              <span className="text-2xl font-bold text-primary">
                {currencySymbol}{total.toLocaleString()}
              </span>
            </div>
            <Link
              to="/checkout"
              onClick={() => dispatch(closeCart())}
              className="block w-full bg-primary text-white text-center py-3 rounded-lg hover:bg-primary-dark transition font-semibold mb-2"
            >
              Proceed to Checkout
            </Link>
            <button
              onClick={() => dispatch(closeCart())}
              className="block w-full bg-gray-200 text-gray-700 text-center py-2 rounded-lg hover:bg-gray-300 transition"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;

